import { Col, Form, Input, Modal, Row } from "antd";
import useNotification from "utils/hooks/useNotification";
import { ApolloQueryResult } from "@apollo/client";
import { getFormError } from "utils/getFormError";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import useBankApi from "./hooks/useBankApi";
import { FaSave } from "react-icons/fa";
import { FaPlus } from "react-icons/fa6";
import React from "react";
import TooltipButton from "views/Documents/components/TooltipButton";
import { MdEdit } from "react-icons/md";

interface CreateUpdateBankPropsTypes {
  refetchListBanks: (
    variables?: Partial<ListBanksInput> | undefined,
  ) => Promise<ApolloQueryResult<ListBanksOutput>>;
  id?: ID;
}

export default function CreateUpdateBank(props: CreateUpdateBankPropsTypes): React.ReactElement {
  const { id, refetchListBanks } = props;
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const urlParams: RouteParams = useParams();

  const [isOpen, setIsOpen] = useState(false);
  const [bankId, setBankId] = useState<ID>();
  const { createUpdateBank, getBank, bankData: data } = useBankApi(bankId);
  const { notificationSuccessfullySaved, notificationError } = useNotification();

  const onFinish = async (values: Record<string, any>) => {
    const updateId = bankId ? { id: bankId } : { companyId: urlParams.companyId };
    let errors: any = [];
    const variables = {
      input: {
        name: values.name,
        ...updateId,
      },
    };

    const response = await createUpdateBank({ variables });

    const data = response?.data as Record<string, { errors?: any[] }> | undefined;
    errors = bankId ? data?.updateBank?.errors : data?.createBank?.errors;

    if (errors && errors.length > 0) {
      const fieldsData = getFormError(errors, values);
      notificationError(errors);
      form.setFields(fieldsData);
    } else {
      refetchListBanks();
      notificationSuccessfullySaved(t("Bank"));
      setIsOpen(false);
      setBankId(undefined);
      form.resetFields();
    }
  };

  useEffect(() => {
    if (bankId) {
      getBank({
        variables: {
          id: bankId,
        },
      });
    }
  }, [bankId]);

  useEffect(() => {
    if (data) {
      form.setFields([
        {
          name: "name",
          value: data?.name,
        },
      ]);
    }
  }, [data]);

  const handleOk = () => {
    form.submit();
  };
  const handleCancel = () => {
    setIsOpen(false);
    setBankId(undefined);
    form.resetFields();
  };

  const handleOnClickEditBank = () => {
    setIsOpen(true);
    setBankId(id);
  };
  const handleOnClickAddBank = () => {
    setIsOpen(true);
  };
  return (
    <>
      <Row gutter={16} justify="space-evenly">
        {id && (
          <Col span={4}>
            <TooltipButton
              title={t("Edit bank")}
              placement="top"
              handleOnClick={handleOnClickEditBank}
              icon={<MdEdit />}
            />
          </Col>
        )}
        <Col span={4}>
          <TooltipButton
            title={t("Create bank")}
            placement="top"
            handleOnClick={handleOnClickAddBank}
            icon={<FaPlus />}
          />
        </Col>
      </Row>
      <Modal
        title={t("Bank")}
        open={isOpen}
        onOk={handleOk}
        okText={t("Save")}
        okButtonProps={{
          icon: (
            <span className="anticon">
              <FaSave />
            </span>
          ),
        }}
        onCancel={handleCancel}
        width={"50%"}
        className="antd-modal"
      >
        <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
          <Row gutter={8}>
            <Col lg={24} sm={24} xs={24}>
              <Form.Item
                label={t("Name")}
                name="name"
                rules={[{ required: true, message: t("This field is required") }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
